import { useCallback, useEffect, useState } from 'react';
import { EmblaCarouselType } from 'embla-carousel';

import { emblaCarouselLastState } from './EmblaCarousel';

type UseSelectedSnapType = {
  snapInitialized: boolean;
  previousSnap: number;
  selectedSnap: number;
  totalSnap: number;
};

export const determineBeforeOrAfter = (
  selectedSnap: number,
  snapCount: number,
  referenceSnap: number,
): number => {
  let beforeDist = 0;
  let afterDist = 0;

  if (selectedSnap === referenceSnap) {
    return 0;
  }

  if (selectedSnap > referenceSnap) {
    beforeDist = selectedSnap - referenceSnap;
    afterDist = referenceSnap + snapCount - selectedSnap;
  } else {
    beforeDist = selectedSnap + snapCount - referenceSnap;
    afterDist = referenceSnap - selectedSnap;
  }

  if (beforeDist > afterDist) {
    return afterDist;
  }

  return -beforeDist;
};

export const useSelectedSnap = (
  snapCount: number,
  totalItemCount: number,
  emblaApi: EmblaCarouselType | undefined,
): UseSelectedSnapType => {
  const [snapInitialized, setSnapInitialized] = useState(false);
  const [previousSnap, setPreviousSnap] = useState(
    emblaCarouselLastState.refSelectedSnap,
  );
  const [selectedSnap, setSelectedSnap] = useState(
    emblaCarouselLastState.refSelectedSnap,
  );
  const [totalSnap, setTotalSnap] = useState(
    emblaCarouselLastState.refTotalSnap,
  );

  const updateScrollSnapState = useCallback(
    (emblaApi_: EmblaCarouselType) => {
      const newPreviousSnap = emblaApi_.previousScrollSnap();
      const newSelectedSnap = emblaApi_.selectedScrollSnap();

      setPreviousSnap(newPreviousSnap);
      setSelectedSnap(newSelectedSnap);
      emblaCarouselLastState.refSelectedSnap = newSelectedSnap;

      const snapOffset = determineBeforeOrAfter(
        newPreviousSnap,
        snapCount,
        newSelectedSnap,
      );
      setTotalSnap((previousTotalSnap) => {
        let newTotalSnap = previousTotalSnap + snapOffset;
        while (newTotalSnap < 0) {
          newTotalSnap += totalItemCount;
        }
        newTotalSnap %= totalItemCount;

        emblaCarouselLastState.refTotalSnap = newTotalSnap;
        return newTotalSnap;
      });

      setSnapInitialized(true);
    },
    [snapCount, totalItemCount],
  );

  useEffect(() => {
    if (!emblaApi) return;

    updateScrollSnapState(emblaApi);
    emblaApi.on('select', updateScrollSnapState);
    emblaApi.on('reInit', updateScrollSnapState);
  }, [emblaApi, updateScrollSnapState]);

  return {
    snapInitialized,
    previousSnap,
    selectedSnap,
    totalSnap,
  };
};
